import { zodResolver } from '@hookform/resolvers/zod';
import type { ActionFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { Form, useActionData } from "@remix-run/react";
import { Buffer } from 'node:buffer';
import { useEffect, useState } from "react";
import { z } from 'zod';
import Container from "~/components/layout/Container";
import { Button } from "~/components/ui/button";
import { Icon } from "~/components/ui/icon";
import { getValidatedFormData, useRemixForm } from "~/lib/remix-hook-form";
import { buildMeta } from "~/lib/seo";

export const ContactFormSchema = z.object({
  adSoyad: z
    .string({ required_error: 'Bu alan boş bırakılamaz.' })
    .min(1, { message: 'Bu alan boş bırakılamaz.' }),
  telefon: z
    .string({ required_error: 'Bu alan boş bırakılamaz.' })
    .min(1, { message: 'Bu alan boş bırakılamaz.' }),
  konu: z
    .string({ required_error: 'Bu alan boş bırakılamaz.' })
    .min(1, { message: 'Bu alan boş bırakılamaz.' }),
  email: z
    .string({ required_error: 'Bu alan boş bırakılamaz' })
    .email({ message: 'Lütfen geçerli bir e-posta adresi giriniz.' })
    .min(1, { message: 'Bu alan boş bırakılamaz.' }),
  mesaj: z
    .string({ required_error: 'Bu alan boş bırakılamaz' })
    .min(1, { message: 'Bu alan boş bırakılamaz.' }),
})

export type ContactFormSchemaType = z.infer<typeof ContactFormSchema>
export const ContactResolver = zodResolver(ContactFormSchema)

export async function action({ request, context }: ActionFunctionArgs) {
  console.log('iletisim formu gonderildi')
  const { errors, data } = await getValidatedFormData<ContactFormSchemaType>(request, ContactResolver)
  if (errors) {
    return {
      success: false,
      data: 'hata',
    }
  }
  const html = `
    <h1>İletişim Formu</h1>
    <p>Ad Soyad: ${data?.adSoyad}</p>
    <p>Telefon: ${data?.telefon}</p>
    <p>Konu: ${data?.konu}</p>
    <p>Email: ${data?.email}</p>
    <p>Mesaj: ${data?.mesaj}</p>
    `
  const frmdata = new FormData();
  frmdata.append("from", context.env.MAILGUN_FROM);
  frmdata.append("to", context.env.MAILGUN_TO);
  frmdata.append("subject", "İletişim Formu");
  frmdata.append("text", "İletişim Formu");
  frmdata.append("html", html);

  let result = false
  const auth = `Basic ${Buffer.from(
    `api:${context.env.MAILGUN_API_KEY}`,
  ).toString('base64')}`

  const results = await fetch(`https://api.eu.mailgun.net/v3/${context.env.MAILGUN_DOMAIN}/messages`, {
    method: 'POST',
    headers: {
      Authorization: auth
    },
    body: frmdata
  })
    .then((response) => {
      console.log('mailgun response', response)
      return response.json() as Promise<{ id?: string, message?: string }>
    })
    .then((sonuc) => {
      console.log('mailgun sonuc aldim', sonuc)
      if (sonuc?.id) {
        result = true
        return {
          success: true,
          data: 'basarili',
        }
      }
      result = false
      return {
        success: false,
        data: 'basarisiz',
      }
    }).catch((error) => {
      console.log('mailgun error', error)
      return {
        success: false,
        data: 'hata',
      }
    })
  return {
    success: result,
    data: result ? 'basarili' : 'basarisiz',
  }
}
export const meta: MetaFunction = () =>
  buildMeta({
    title: "İletişim | Al-De Mühendislik",
    description:
      "Al-De Mühendislik ile iletişime geçin. Manisa, İzmir, Turgutlu ve Akhisar'da ısıtma, doğalgaz ve mekanik tesisat hizmetleri için teklif alın. Telefon: +90 544 366 98 08.",
    path: "/iletisim",
  })
export default function Page() {
  const actionData = useActionData<typeof action>()
  const [result, setResult] = useState<string>('')

  const { register, handleSubmit, formState: { errors }, reset } = useRemixForm<ContactFormSchemaType>({
    mode: 'onSubmit',
    resolver: ContactResolver,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    console.log('actionData', actionData)
    if (actionData?.success) {
      if (actionData.data === 'basarili') {
        setResult('Mesajınız gönderildi')
        reset()
      } else {
        setResult('Mesajınız gönderilemedi')
      }
    }
  }, [actionData])
  return (
    <div className="py-[122px]">
      <Container className="flex-wrap py-18">
        <div className="w-full py-12 border-b border-gray-100">
          <h1 className="text-[36px] font-bold mb-4 font-source-sans-pro pb-4 flex justify-center w-full">İletişim</h1>
          <p className="text-[16px] font-light block text-center">
            Al-De Mühendislik olarak sizlere hizmet vermekteyiz. İhtiyaç duyduğunuzda bize ulaşın.
          </p>
        </div>
        <div className="flex flex-wrap w-full p-0 md:p-12 py-10 md:py-0 !pt-10">
          <div className="w-full md:w-1/2 mb-10 md:mb-0">
            <h2 className="text-[24px] font-bold mb-4">İletişim Bilgileri</h2>
            <address className="text-[18px] not-italic font-source-sans-pro  [&_span]:mb-2 [&_span]:flex [&_span]:items-center [&_span]:gap-2 [&_svg]:text-red-700 [&_a]:hover:text-red-700">
              <span>
                <small>
                  <Icon name="material-symbols:phone-in-talk-watchface-indicator-sharp" />
                </small>
                Telefon: <a href="tel:+905443669808">+90 544 366 98 08</a></span>
              <span>
                <small>
                  <Icon name="mingcute:whatsapp-fill" />
                </small>
                WhatsApp: <a href="https://wa.me/905443669808">+90 544 366 98 08</a></span>
              <span>
                <small>
                  <Icon name="bxs:envelope" />
                </small>
                Email: <a href="mailto:bilgi@aldemuhendislik.com">bilgi@aldemuhendislik.com</a></span>
            </address>
          </div>
          <div className="w-full md:w-1/2">
            <h3 className="text-[24px] font-bold mb-4">İletişim Formu</h3>
            <Form className={styles.form} method="post" onSubmit={handleSubmit}>
              <div className={styles.shortDiv}>
                <label htmlFor="name">Adınız</label>
                <input type="text" id="name" {...register('adSoyad')} />
                {errors.adSoyad && <p className="text-red-500">{errors.adSoyad.message}</p>}
              </div>
              <div className={styles.shortDiv}>
                <label htmlFor="email">Email</label>
                <input type="email" id="email" {...register('email')} />
                {errors.email && <p className="text-red-500">{errors.email.message}</p>}
              </div>
              <div className={styles.shortDiv}>
                <label htmlFor="phone">Telefon</label>
                <input type="text" id="phone" {...register('telefon')} />
                {errors.telefon && <p className="text-red-500">{errors.telefon.message}</p>}
              </div>
              <div className={styles.shortDiv}>
                <label htmlFor="subject">Konu</label>
                <input type="text" id="subject" {...register('konu')} />
                {errors.konu && <p className="text-red-500">{errors.konu.message}</p>}
              </div>
              <div className={styles.longDiv}>
                <label htmlFor="message">Mesajınız</label>
                <textarea id="message" {...register('mesaj')} className="h-24 w-full" />
                {errors.mesaj && <p className="text-red-500">{errors.mesaj.message}</p>}
              </div>
              <div className={styles.longDiv}>
                {result && <p className="text-green-500">{result}</p>}
                <Button variant="success" className=" text-white px-4 py-2 rounded-md">Gönder</Button>
              </div>
            </Form>
          </div>
        </div>
      </Container>
    </div>
  )
}
const styles = {
  label: '',
  shortDiv: `w-6/12
  [&_label]:block [&_label]:text-[16px] [&_label]:font-light [&_label]:mb-2
   even:pl-8`,
  longDiv: 'w-full',
  form: `flex flex-wrap w-full
  [&_input]:w-[98%] [&_input]:p-2 [&_input]:border [&_input]:border-gray-300 [&_input]:rounded-md [&_input]:mb-4 [&_textarea]:w-full [&_textarea]:p-2 [&_textarea]:border [&_textarea]:border-gray-300 [&_textarea]:rounded-md [&_textarea]:mb-4 [&_textarea]:h-24`
}
